/**
 * 메타/네이버 광고 엑셀(XLSX) → 광고비 수동 입력 draft 행 변환.
 *
 * - mapRowsToDrafts: 원본 행 객체 배열 → draft 행 (순수 함수, 테스트 대상).
 * - parseAdSpendXlsx: File → draft 행 (xlsx 동적 import 래퍼).
 *
 * 저장은 기존 POST /api/admin/dashboard/ads/spend 를 재사용하므로
 * 출력 형태(AdSpendDraft)는 AdReport 의 DraftRow(수치=문자열)와 호환된다.
 *
 * [보안 — 수용된 위험] npm 배포판 xlsx@0.18.5 에는 알려진 취약점이 있다
 * (CVE-2023-30533 prototype pollution, CVE-2024-22363 ReDoS). 수정본은
 * SheetJS 공식 CDN 에만 있고 npm 에는 미게시라, 우리는 위협모델상 잔여
 * 위험이 낮다고 판단해 npm 판을 그대로 사용한다: 파싱 대상은 관리자 본인이
 * 광고 관리자에서 내려받은 신뢰된 파일이고, 파싱은 브라우저(클라이언트)에서만
 * 일어나며 파일은 서버로 전송되지 않는다. 향후 xlsx 를 신뢰할 수 없는 입력에
 * 쓰게 되면 CDN 수정본으로 교체하거나 대체 라이브러리로 이전해야 한다.
 */

export type AdChannel = 'META' | 'NAVER'

/** 편집 모달에 채울 파싱 결과 행. key(UI 식별자)는 페이지가 부여한다. */
export interface AdSpendDraft {
  channel: AdChannel
  spendDate: string // YYYY-MM-DD
  spend: string // 정수 문자열 (KRW)
  conversionValue: string // 전환매출 정수 문자열 (KRW)
  impressions: string // 정수 문자열
  clicks: string // 정수 문자열
}

export interface ParseResult {
  rows: AdSpendDraft[]
  warnings: string[]
}

/** 업로드 1회 최대 일수 (API items max 200 과 일치). */
const MAX_ROWS = 200

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** 채널별 컬럼 별칭(정규화 후 비교). NAVER 는 실물 확보 후 채운다. */
const ALIASES: Record<AdChannel, { date: string[]; spend: string[]; conversion: string[]; impressions: string[]; clicks: string[] }> = {
  META: {
    date: ['day', 'date', 'reporting starts', '보고 시작', '보고 종료', '일', '일자', '날짜'],
    spend: ['amount spent (krw)', 'amount spent', 'spend', '지출 금액 (krw)', '지출 금액', '지출금액', '광고비'],
    conversion: [
      'purchases conversion value', 'website purchases conversion value', 'purchase conversion value',
      'conversion value', '구매 전환값', '구매 전환 값', '전환값', '전환 값', '웹사이트 구매 전환값', '구매 전환매출',
    ],
    impressions: ['impressions', '노출', '노출수'],
    clicks: ['clicks (all)', 'clicks', '클릭(전체)', '클릭', '클릭수'],
  },
  NAVER: { date: [], spend: [], conversion: [], impressions: [], clicks: [] },
}

/** 헤더/별칭 정규화: 소문자 + 앞뒤공백 제거 + 내부 연속공백 1칸. */
function normHeader(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, ' ')
}

/** 광고비 컬럼 접두 fallback: 통화 접미('(KRW)' 등) 변형을 흡수. 정규화 기준. */
const SPEND_PREFIXES = ['amount spent', '지출 금액', '지출금액']

/** headers 중 aliases 와 정규화 일치하는 첫 컬럼. spend 는 접두 fallback 허용. */
function findColumn(headers: string[], aliases: string[], allowSpendPrefix = false): string | null {
  const normAliases = aliases.map(normHeader)
  for (const h of headers) {
    if (normAliases.includes(normHeader(h))) return h
  }
  if (allowSpendPrefix) {
    for (const h of headers) {
      const nh = normHeader(h)
      if (SPEND_PREFIXES.some((p) => nh.startsWith(p))) return h
    }
  }
  return null
}

/** "152,300" | " ₩1,234 " | 152300 → number. 숫자 아님/빈값 → null. */
function parseNum(raw: unknown): number | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const cleaned = String(raw).replace(/[^0-9.-]/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function isRealDate(ymd: string): boolean {
  const [, m, d] = ymd.split('-').map(Number)
  return m >= 1 && m <= 12 && d >= 1 && d <= 31
}

/** 다양한 날짜 표기 → YYYY-MM-DD. 실패 시 null. */
function normalizeDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null
  const s = String(raw).trim().replace(/\.$/, '') // 끝 마침표 제거 ("2026. 7. 10.")
  if (YMD_RE.test(s)) return isRealDate(s) ? s : null
  const parts = s.split(/[.\/\-\s]+/).filter(Boolean)
  if (parts.length === 3 && /^\d{4}$/.test(parts[0])) {
    const out = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    return YMD_RE.test(out) && isRealDate(out) ? out : null
  }
  return null
}

/** 원본 행 객체 배열 → draft 행 (순수). 실패 조건은 throw. */
export function mapRowsToDrafts(rawRows: Record<string, unknown>[], channel: AdChannel): ParseResult {
  if (!rawRows.length) throw new Error('가져올 유효한 행이 없습니다.')

  const headerSet = new Set<string>()
  for (const r of rawRows) for (const k of Object.keys(r)) headerSet.add(k)
  const headers = [...headerSet]

  const cfg = ALIASES[channel]
  const dateCol = findColumn(headers, cfg.date)
  const spendCol = findColumn(headers, cfg.spend, channel === 'META')
  if (!dateCol || !spendCol) {
    throw new Error('엑셀에서 날짜·지출 금액 컬럼을 찾지 못했습니다. 올바른 광고 관리자 내보내기 파일인지 확인하세요.')
  }
  const convCol = findColumn(headers, cfg.conversion)
  const imprCol = findColumn(headers, cfg.impressions)
  const clickCol = findColumn(headers, cfg.clicks)

  const warnings: string[] = []
  const byDate = new Map<string, { spend: number; conversionValue: number; impressions: number; clicks: number }>()

  rawRows.forEach((row, i) => {
    const rowNo = i + 1
    const date = normalizeDate(row[dateCol])
    if (!date) {
      warnings.push(`데이터 ${rowNo}행: 날짜를 인식할 수 없어 건너뜀`)
      return
    }
    const spend = parseNum(row[spendCol])
    if (spend == null || spend < 0) {
      warnings.push(`데이터 ${rowNo}행(${date}): 광고비 값이 없거나 잘못되어 건너뜀`)
      return
    }
    // 메타 KRW 광고비는 정수여야 한다. 소수면 조용히 반올림하지 않고 건너뛰고 경고.
    if (Math.floor(spend) !== spend) {
      warnings.push(`데이터 ${rowNo}행(${date}): 광고비가 정수가 아니어서 건너뜀`)
      return
    }
    // 전환매출은 소수(원 단위 반올림)를 허용 — 메타 전환값은 소수로 내려올 수 있음.
    const conv = Math.max(0, Math.round(parseNum(convCol ? row[convCol] : null) ?? 0))
    const impr = Math.max(0, parseNum(imprCol ? row[imprCol] : null) ?? 0)
    const clk = Math.max(0, parseNum(clickCol ? row[clickCol] : null) ?? 0)
    const prev = byDate.get(date) ?? { spend: 0, conversionValue: 0, impressions: 0, clicks: 0 }
    byDate.set(date, {
      spend: prev.spend + spend,
      conversionValue: prev.conversionValue + conv,
      impressions: prev.impressions + impr,
      clicks: prev.clicks + clk,
    })
  })

  if (byDate.size === 0) throw new Error('가져올 유효한 행이 없습니다.')
  if (byDate.size > MAX_ROWS) {
    throw new Error(`한 번에 최대 ${MAX_ROWS}일까지 업로드할 수 있습니다. (현재 ${byDate.size}일)`)
  }

  const rows: AdSpendDraft[] = [...byDate.entries()].map(([spendDate, v]) => ({
    channel,
    spendDate,
    spend: String(Math.round(v.spend)),
    conversionValue: String(Math.round(v.conversionValue)),
    impressions: String(Math.round(v.impressions)),
    clicks: String(Math.round(v.clicks)),
  }))
  return { rows, warnings }
}

/** File(XLSX) → draft 행. xlsx 는 동적 import(초기 번들 제외). */
export async function parseAdSpendXlsx(file: File, channel: AdChannel): Promise<ParseResult> {
  let XLSX: typeof import('xlsx')
  try {
    XLSX = await import('xlsx')
  } catch {
    throw new Error('파일 처리 모듈을 불러오지 못했습니다. 새로고침 후 다시 시도하세요.')
  }

  let wb: import('xlsx').WorkBook
  try {
    const buf = await file.arrayBuffer()
    // raw:false + dateNF 로 날짜 셀을 'YYYY-MM-DD' 문자열로 통일(타임존 오차 회피).
    wb = XLSX.read(buf, { type: 'array', cellDates: false })
  } catch {
    throw new Error('엑셀 파일을 읽을 수 없습니다.')
  }

  const sheetName = wb.SheetNames[0]
  if (!sheetName) throw new Error('워크시트가 없는 빈 엑셀 파일입니다.')
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[sheetName], {
    raw: false,
    dateNF: 'yyyy-mm-dd',
    defval: '',
  })
  return mapRowsToDrafts(rawRows, channel)
}
